import { type ReactNode, useCallback } from "react";

import { AssetTypeAmazonWebServices, AssetTypeGoogleCloud, AssetTypeMicrosoftAzure } from "@doitintl/cmp-models";
import { Alert, Box } from "@mui/material";
import intersection from "lodash/intersection";

import { cloudAnalyticsText } from "../../assets/texts";
import { useCloudAnalyticsMetadata } from "../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { CircularProgressLoader } from "../../Components/Loader";
import { useCustomerContext } from "../../Context/CustomerContext";
import { type ReportWSnap } from "../../types";
import { AllocationBrowser } from "./allocations/AllocationBrowser/AllocationBrowser";
import { AllocationsCreate } from "./allocations/AllocationsCreate";
import { AllocationsEdit } from "./allocations/AllocationsEdit";
import { BudgetBrowser } from "./budgets/BudgetBrowser";
import { ReportDimensionsContextProvider } from "./Context";
import { LabelsBrowser } from "./labels/LabelsBrowser";
import { MetricBrowser } from "./metrics";
import { ReportBrowser } from "./reports/ReportBrowser";
import ReportTemplatesBrowser from "./templateLibrary/ReportTemplatesBrowser";
import { type ReportTemplateCard } from "./templateLibrary/types";

const customerSupportedAssetTypes = [AssetTypeAmazonWebServices, AssetTypeGoogleCloud, AssetTypeMicrosoftAzure];

type Props = {
  reports: ReportWSnap[];
  reportsLoading: boolean;
  attributionsLoading: boolean;
  pageId: string;
  reportTemplateCards: ReportTemplateCard[];
};

export function AnalyticsPages({ reports, reportsLoading, pageId, reportTemplateCards }: Props) {
  const { customerOrPresentationModeCustomer: customer, isProductOnlyCustomer } = useCustomerContext();
  const { hasMetadata } = useCloudAnalyticsMetadata();

  const isFSStandaloneCustomer =
    isProductOnlyCustomer && (customer?.enabledFlexsave?.AWS || customer?.enabledFlexsave?.GCP);
  const isSaaSConsoleCustomer =
    isProductOnlyCustomer && (customer?.enabledSaaSConsole?.AWS || customer?.enabledSaaSConsole?.GCP);

  let Page: ReactNode | null = null;
  switch (pageId) {
    case "analytics:reports":
      Page = <ReportBrowser reports={reports} loading={reportsLoading} />;
      break;
    case "analytics:report-templates":
      Page = (
        <ReportDimensionsContextProvider>
          <ReportTemplatesBrowser reportTemplateCards={reportTemplateCards} />
        </ReportDimensionsContextProvider>
      );
      break;
    case "analytics:allocations":
      Page = <AllocationBrowser />;
      break;
    case "analytics:allocations:create":
      Page = <AllocationsCreate />;
      break;
    case "analytics:allocations:details":
      Page = <AllocationsEdit />;
      break;
    case "analytics:metrics":
      Page = <MetricBrowser reports={reports} />;
      break;
    case "analytics:budgets":
      Page = <BudgetBrowser />;
      break;
    case "analytics:labels":
      Page = <LabelsBrowser />;
      break;
    case "analytics:reports:labels":
      Page = <LabelsBrowser />;
      break;
    case "analytics:metrics:labels":
      Page = <LabelsBrowser />;
      break;
    case "analytics:allocations:labels":
      Page = <LabelsBrowser />;
      break;
    default:
  }

  const noMetadataAlert = useCallback(() => {
    let message;

    if (isFSStandaloneCustomer) {
      message = cloudAnalyticsText.ANALYTICS_TABS_ALERT_STANDALONE;
    } else if (isSaaSConsoleCustomer) {
      message = cloudAnalyticsText.ANALYTICS_TABS_ALERT_SAAS_CONSOLE;
    } else {
      message =
        intersection(customer?.assets ?? [], customerSupportedAssetTypes).length > 0
          ? cloudAnalyticsText.ANALYTICS_TABS_ALERT
          : cloudAnalyticsText.ANALYTICS_TABS_ALERT_NO_ASSETS;
    }

    return (
      <Box>
        <Alert severity="warning">{message}</Alert>
      </Box>
    );
  }, [customer.assets, isFSStandaloneCustomer, isSaaSConsoleCustomer]);

  if (!Page) {
    return;
  }

  if (hasMetadata === undefined) {
    return <CircularProgressLoader />;
  }

  return <Box>{!hasMetadata ? noMetadataAlert() : Page}</Box>;
}
